import Vue from 'vue';
import AccountFlyout, { AccountNavButton, AccountLink, SignInNudge } from '@rei/account-flyout';
import { getUser } from '@rei/session';
import getAdobeTargetJsonOffer from '@rei/adobe-target-functions';
import AccountLinkFragment from './components/AccountLinkFragment.vue';
import LoadingFragment from './components/LoadingFragment.vue';

const gNav = document.querySelector('.gnav');
const isWhiteNav = gNav !== null;

let isAccountMounted = false;
let isSignInNudgeMounted = false;

const accountEventBus = new Vue({});
const signInNudgeEventBus = new Vue({});

let user = null;

if (isWhiteNav) {
  /* This is a default loading fragment that will display first and
    visually communicate to the user that something related to their
    account login is still pending.
  */
  new Vue({
    el: '[data-ui="login-module-greeting"]',
    render(app) {
      return app(LoadingFragment, {});
    },
  });

  user = getUser()
    .then((userObj) => {
      user = userObj;
      if (user && user.isLoggedIn) {
        new Vue({
          el: '[data-ui="login-module-greeting"]',
          data: { showRewardsNav: false },
          computed: {
            userGreeting() {
              if (user
                  && user.contact
                  && user.contact.firstName) {
                return `Hi, ${user.contact.firstName}`;
              }
              return 'My Account';
            },
            rewardsBalance() {
              if (user
                && user.membership
                && user.membership.dividendBalance != null) {
                const {
                  membership: {
                    dividendBalance,
                  },
                } = user;
                const balance = parseFloat(dividendBalance);
                const formattedBalance = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(balance);
                return formattedBalance === '$0.00' ? '' : `${formattedBalance}`;
              }
              return '';
            },
          },
          async mounted() {
            try {
              const offers = await getAdobeTargetJsonOffer('rewards-in-nav-component');
              if (offers.length) {
                const [{ content }] = offers;
                const [[{ toggleRewardsInNav }]] = content;
                if (toggleRewardsInNav) { this.showRewardsNav = true; }
              }
            } catch (e) {
              console.error('Error with getAdobeTargetJsonOffer', e);
            }
            this.initCustomerName();
          },
          methods: {
            initCustomerName() {
              // Replace mobile menu default text when user is logged in
              // also allows user to log out on mobile screen sizes
              /* TODO: pull this out into account-flyout package */
              const loginButton = document.querySelector('li[data-js="mobile-nav-login-link"]');
              if (loginButton) {
                loginButton.innerHTML = `<div class="account-link-mobile-container">
                                      <a class="account-link" href="/YourAccountInfoInView" data-ui=nav-level-1" data-analytics-id="rei_nav:account" rel="nofollow">
                                        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" data-visible="mobile" class="gnav-icon" aria-hidden="true"> <use xlink:href="#account-profile"></use> </svg>
                                        <div class="account-link-info-container">
                                          <span class="account-link__text" data-icon="left">${this.userGreeting}</span>
                                          ${this.showRewardsNav && this.rewardsBalance ? `<span class="account-link__text account-link__rewards-wrapper" data-icon="left">
                                                  Rewards: 
                                                  <span class="account-link__rewards-span">${this.rewardsBalance}</span>
                                                </span>` : ''}
                                        </div>
                                      </a>
                                    <a class="account-link account-link--right" href="/youraccount/logout" data-ui="nav-level-1" data-analytics-id="rei_nav:sign_out" rel="nofollow">
                                      <span class="account-link__text">Sign Out</span>
                                    </a>
                                  </div>`;
              }
            },
          },
          render(app) {
            return app(AccountNavButton, {
              props: {
                userGreeting: this.userGreeting,
                rewardsBalance: this.rewardsBalance,
                showRewardsNav: this.showRewardsNav,
              },
            });
          },
        });
      } else {
        new Vue({
          el: '[data-ui="login-module-greeting"]',
          render(app) {
            return app(AccountLinkFragment);
          },
        });
      }

      const accountToggleButton = document.querySelector("[data-ui='site-header-action-local-account']");
      const navbarWrapper = document.querySelector("[data-ui='site-navigation']");

      // Mount account and set event listeners, this function will only be executed if the account
      // button is hover or focused. Otherwise there is no need to mount component
      const mountAccount = () => {
        isAccountMounted = true;

        const data = {
          open: false,
        };

        // Insert <li> element next to account toggle button to render Account component
        accountToggleButton.parentNode.insertAdjacentHTML('afterEnd', `<li class="account-dropdown ${isWhiteNav && 'account-dropdown--white-nav'}"><div id="account-placement"></div></li>`);
        const accountDropdown = document.querySelector('.account-dropdown');

        new Vue({
          el: '#account-placement',
          data,
          mounted() {
            accountEventBus.$on('closed', (escKeyPressed) => this.handleClose(escKeyPressed));

            accountToggleButton.setAttribute('aria-expanded', 'false');
            accountToggleButton.setAttribute('aria-controls', 'account-flyout');
            accountToggleButton.addEventListener('click', this.toggleButtonHandler);
            window.addEventListener('click', this.clickOutsideHandler, true);
          },
          beforeDestroy() {
            accountEventBus.$off('closed', this.handleClose);

            accountToggleButton.removeEventListener('click', this.toggleButtonHandler);
            window.removeEventListener('click', this.clickOutsideHandler, true);
          },
          methods: {
            toggle() {
              this.open = !this.open;
            },
            handleClose(escapeKeyPressed) {
              this.open = false;
              accountToggleButton.setAttribute('aria-expanded', 'false');
              navbarWrapper.style.overflowY = 'initial';
              const isFocusOnBody = document.activeElement.tagName === 'BODY';
              if (isFocusOnBody || escapeKeyPressed) { accountToggleButton.focus(); }

              if (isWhiteNav) {
                gNav.setAttribute('data-account', 'false');
                document.body.classList.remove('no-scroll');
              }
            },
            toggleButtonHandler(event) {
              event.preventDefault();
              event.stopPropagation();

              const { clientWidth } = window.document.body;

              this.toggle();
              document.body.classList.remove('sign-in-open');
              accountToggleButton.setAttribute('aria-expanded', this.open ? 'true' : 'false');
              navbarWrapper.style.overflowY = this.open && clientWidth < 992 ? 'hidden' : 'initial';
              accountDropdown.style.display = this.open ? 'block' : 'none';

              if (isWhiteNav) {
                gNav.setAttribute('data-account', this.open ? 'true' : 'false');

                if (this.open) {
                  document.body.classList.add('no-scroll');
                } else {
                  document.body.classList.remove('no-scroll');
                }
              }
            },
            clickOutsideHandler(event) {
              // Avoid closing component if user clicked on any of those elements
              const excludeElements = [
                'login-module-my-account',
                'login-module-purchase-history',
                'login-module-your-lists',
                'login-module-sign-out',
                'site-header-action-local-account',
              ];

              if (!this.open || excludeElements.includes(event.target.getAttribute('data-ui')) || accountToggleButton.contains(event.target)) return;

              if (!accountDropdown.contains(event.target)) {
                accountEventBus.$emit('closed');
              }
            },
          },
          render: (app) => app(AccountFlyout, {
            props: {
              open: data.open,
              customClasses: ['account-wrapper'],
              trapTabInsideWidget: true,
              user,
            },
            on: {
              closed() {
                accountEventBus.$emit('closed');
              },
              'on-escape': function () {
                accountEventBus.$emit('closed', 'escape key was pressed');
              },
            },
          }),
        });
      };

      const signInLink = document.querySelector("[data-ui='login-module-greeting']");

      const mountSignInNudge = () => {
        isSignInNudgeMounted = true;

        signInLink.parentNode.insertAdjacentHTML('afterEnd', `<li class="sign-in-nudge-dropdown ${isWhiteNav && 'sign-in-nudge--white-nav'}"><div id="sign-in-nudge-placement"></div></li>`);
        const signInNudge = document.querySelector('.sign-in-nudge-dropdown');
        new Vue({
          el: '#sign-in-nudge-placement',
          data: {
            open: false,
            shouldDisplayMessage: false,
          },
          async mounted() {
            try {
              const offers = await getAdobeTargetJsonOffer('sign-in-nudge-component');
              if (offers.length) {
                const [{ content }] = offers;
                const [[{ toggleSignInNudge }]] = content;
                if (toggleSignInNudge) { this.shouldDisplayMessage = true; }
              }
            } catch (e) {
              console.error('Error with getAdobeTargetJsonOffer', e);
            }
            this.shouldSignInNudgeLoad();
            signInNudgeEventBus.$on('closed', this.handleClose);

            signInLink.setAttribute('aria-expanded', 'false');
            signInLink.setAttribute('aria-controls', 'sign-in-nudge');
            window.addEventListener('click', this.clickOutsideHandler, true);
          },
          beforeDestroy() {
            signInNudgeEventBus.$off('closed', this.handleClose);
            window.removeEventListener('click', this.clickOutsideHandler, true);
          },
          methods: {
            toggle() {
              this.open = !this.open;
            },
            handleClose() {
              this.open = false;
              signInLink.setAttribute('aria-expanded', 'false');
              navbarWrapper.style.overflowY = 'initial';
              const nudgeClasses = ['sign-in-nudge__container--flyout-login', 'sign-in-nudge__container--flyout-registration-link'];
              const isFocusOnNudge = nudgeClasses.indexOf(document.activeElement.classList[0]) > -1;
              if (isFocusOnNudge) { signInLink.focus(); }

              if (isWhiteNav) {
                gNav.setAttribute('data-nudge', 'false');
                document.body.classList.remove('no-scroll');
                signInNudge.style.display = 'none';
              }
            },
            clickOutsideHandler(event) {
              const excludeElements = [
                'login-module-greeting',
              ];

              if (!this.open || excludeElements.includes(event.target.getAttribute('data-ui')) || signInLink.contains(event.target)) return;

              if (!signInNudge.contains(event.target)) {
                signInNudgeEventBus.$emit('closed');
              }
            },
            shouldSignInNudgeLoad() {
              const mainLastOpenedKey = 'login-opened';
              const cartLastOpenedKey = 'cart-opened';
              const isDesktop = window.matchMedia('(min-width: 992px)').matches;
              const isHomePage = ['', '/'].indexOf(window.location.pathname) > -1;
              const isCorePDP = !!window.location.pathname.match(/^\/product/);
              const isCart = ['', '/ShoppingCart'].indexOf(window.location.pathname) > -1;
              if (!(isHomePage || isCorePDP || isCart) || !isDesktop) return;
              if (isHomePage || isCorePDP) this.checkTimeSinceNudge(mainLastOpenedKey);
              if (isCart) this.checkTimeSinceNudge(cartLastOpenedKey);
            },
            checkTimeSinceNudge(lastOpenedKey) {
              const thirtyMinuteMs = 30 * 60 * 1000;
              const thirtyMinutesAgo = +new Date() - thirtyMinuteMs;

              const nudgeLastOpened = parseInt(
                window.localStorage.getItem(lastOpenedKey),
                10,
              );

              const openedWithinLast30Min = nudgeLastOpened > thirtyMinutesAgo;
              // Boolean to check if myStore is open to avoid showing nudge
              const myStoreOpen = localStorage.getItem('myStoreOpen') && localStorage.getItem('myStoreOpen') === 'true';

              if (openedWithinLast30Min || user?.isLoggedIn || myStoreOpen) return;

              window.localStorage.setItem(lastOpenedKey, String(+new Date()));

              this.openSignInNudge();
            },
            openSignInNudge() {
              const { clientWidth } = window.document.body;
              this.toggle();
              document.body.classList.remove('sign-in-open');
              signInLink.setAttribute('aria-expanded', 'true');
              navbarWrapper.style.overflowY = clientWidth < 992 ? 'hidden' : 'initial';
              signInNudge.style.display = 'block';

              if (isWhiteNav) {
                gNav.setAttribute('data-nudge', 'true');
                document.body.classList.add('no-scroll');
              }
            },
          },
          render(app) {
            return app(SignInNudge, {
              props: {
                open: this.open,
                trapTabInsideWidget: false,
                shouldDisplayMessage: this.shouldDisplayMessage,
              },
              on: {
                closed() {
                  signInNudgeEventBus.$emit('closed');
                },
                'on-escape': function () {
                  signInNudgeEventBus.$emit('closed');
                },
              },
            });
          },
        });
      };

      if (accountToggleButton && navbarWrapper && isWhiteNav) {
        ['focus', 'mouseenter'].forEach((eventName) => accountToggleButton.addEventListener(eventName, () => {
          if (!isAccountMounted) {
            mountAccount();
          }
        }, {
          once: true,
        }));

        window.addEventListener('load', () => {
          if (!isAccountMounted) {
            mountAccount();
          }
        });
      }

      if (signInLink && navbarWrapper && isWhiteNav) {
        setTimeout(() => {
          if (!isSignInNudgeMounted) {
            mountSignInNudge();
          }
        }, 2000);
      }
    })
    .catch(() => {
      new Vue({
        el: '[data-ui="login-module-greeting"]',
        render(app) {
          return app(AccountLink);
        },
      });
    });
}
